// §9.3 — what a validated inbound message is *done with*.
//
// The bridge host establishes that a message is real: right window, right origin, right token,
// right shape, after the handshake. This is the next step and the last one — it turns a
// `<channel>/<type>` into a call on `HangarApiHost`, the **same** object `createInprocApi` binds a
// built-in to. That is §4.1's one-api-two-transports claim (D-006) made structural: a message that
// reached here does exactly what the in-process call of the same name does, because it *is* it.
//
// Two shapes come back out:
//
//   • **emit** — `window/title` and friends. The client posts and does not wait; nothing is sent.
//   • **call** — the client is awaiting `<channel>/reply` with `{to, ok, value?}`. A thrown
//     `HangarApiError` becomes `{ok: false, code, message}`, which is what the client re-throws.
//
// Channels arrive one milestone at a time. `ROUTED_CHANNELS` is the list, and anything outside it
// is counted and ignored rather than answered — an app waiting on a channel that does not exist yet
// waits, which is honest, where a fabricated reply would not be.
import {
  HangarApiError,
  type DecodedMessage,
  type ExecFrame,
  type MenuNode,
  type Payload,
  type PickOpts,
  type VfsPath,
} from '@hangar/shared';
import { wirePayloadIn, wirePayloadOut } from '../transfer/wire';
import type { AppInstanceContext, HangarApiHost } from '../api/port';
import type { BridgeConnection } from './host';

/**
 * The channels this router answers on. One entry per milestone, in §9.3's order.
 *
 * `dialog` is here at M8.8 rather than at a milestone of its own because no task assigns it and
 * M8.8's *own* verify needs it: §18 F9's veto is only observable through the prompt the app raises
 * while deciding, and §19.4 says the shell draws that prompt (D-120).
 */
export const ROUTED_CHANNELS = [
  'window',
  'dialog',
  'menu',
  'fs',
  'session',
  'keys',
  'transfer',
  'notify',
  'exec',
] as const;

export interface BridgeRouterDeps {
  apiHost: HangarApiHost;
  /** The window a connection belongs to. Its id is what `AppInstanceContext` needs. */
  winIdFor: (instanceKey: string) => string | undefined;
  /**
   * §9.7 step 3 — a reserved chord the app caught and handed back, for the shell to run as if it
   * had received the event itself. Not a `HangarAPI` call: nothing about it is the app's to do.
   */
  onChord?: (connection: BridgeConnection, chord: string) => void;
  /** Counted for App Inspector (§11.12): a message on a channel no milestone has routed yet. */
  onUnrouted?: (connection: BridgeConnection, key: string) => void;
  /** §11.2 — one frame of a command this app is running for the terminal (M13.2). */
  onExecFrame?: (connection: BridgeConnection, to: string, frame: ExecFrame) => void;
}

export interface BridgeRouter {
  (connection: BridgeConnection, message: DecodedMessage): void;
  /**
   * Stops every `fs.watch` this window held. Called when the window closes.
   *
   * A watcher is the one thing on this channel that outlives the call that made it, and §9.5
   * gives a window's reach exactly the lifetime of the window — the same event that revokes its
   * picker grants has to end its subscriptions, or the shell keeps pushing `fs/changed` at a
   * frame that no longer exists.
   */
  release(instanceKey: string): void;
}

/** Every channel that answers a call does so on `<channel>/reply`, uniformly (D-112). */
type ReplyKey =
  | 'window/reply'
  | 'menu/reply'
  | 'dialog/reply'
  | 'fs/reply'
  | 'session/reply'
  | 'transfer/reply'
  | 'notify/reply';

/** §4.1 — an error the app can act on, however it arrived. */
function failure(error: unknown): { code: string; message: string } {
  if (error instanceof HangarApiError) return { code: error.code, message: error.message };
  return { code: 'UNKNOWN', message: error instanceof Error ? error.message : String(error) };
}

function badRequest(message: string): HangarApiError {
  return new HangarApiError('BAD_REQUEST', message);
}

/**
 * §9.4 — rebuild `PickOpts` from the wire rather than forwarding what arrived.
 *
 * Every other payload on this channel is shaped by §9.3's table before it reaches here; this one
 * is inside an `unknown[]`, so it is the one place a frame can hand the kernel an object of its
 * own choosing. Each field is taken only if it is the type it claims to be, and nothing else
 * travels — an `extensions: [{}]` would otherwise reach the gateway's `.toLowerCase()`.
 */
function pickOptsFrom(raw: unknown): PickOpts | undefined {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    throw badRequest('fs picker options must be an object');
  }
  const source = raw as Record<string, unknown>;
  const opts: PickOpts = {};
  if (typeof source['title'] === 'string') opts.title = source['title'];
  if (typeof source['startIn'] === 'string') opts.startIn = source['startIn'];
  if (typeof source['suggestedName'] === 'string') opts.suggestedName = source['suggestedName'];
  if (typeof source['multiple'] === 'boolean') opts.multiple = source['multiple'];
  if (typeof source['directory'] === 'boolean') opts.directory = source['directory'];
  if (Array.isArray(source['extensions'])) {
    opts.extensions = source['extensions'].filter(
      (extension): extension is string => typeof extension === 'string',
    );
  }
  return opts;
}

export function createBridgeRouter(deps: BridgeRouterDeps): BridgeRouter {
  /**
   * Live `fs.watch` subscriptions: window instance → subscription id → how to stop it.
   *
   * Keyed by instance rather than by connection object so `release` can be driven by the same
   * `window:closed` event everything else in this lifetime is driven by.
   */
  const subscriptions = new Map<string, Map<string, () => void>>();
  let nextSubscription = 0;

  /** Answer a call-shaped message once its work settles. */
  function respond(
    connection: BridgeConnection,
    message: DecodedMessage,
    key: ReplyKey,
    work: () => unknown,
  ): void {
    void Promise.resolve()
      .then(work)
      .then(
        (value) => {
          connection.send(key, { to: message.envelope.id, ok: true, value });
        },
        (error: unknown) => {
          connection.send(key, { to: message.envelope.id, ok: false, ...failure(error) });
        },
      );
  }

  /**
   * §9.3 `fs/call` — `{method, args}` becomes one `HangarApiHost.fs` call.
   *
   * A **switch, not a property read on `api.fs`**. `method` comes off the wire, and
   * `{method: 'constructor'}` against a bare index would reach something that is not a filesystem
   * call at all. Arguments are checked here for the same reason: §9.3's table types the payload
   * down to `unknown[]`, and the gateway is entitled to be handed a path that is a string.
   *
   * Scoping itself is not done here and must not be — it is `vfs.confine` behind every one of
   * these, exactly as it is for a built-in calling in process (§9.5, §7.1: one implementation).
   */
  function callFs(
    connection: BridgeConnection,
    ctx: AppInstanceContext,
    method: string,
    args: readonly unknown[],
  ): unknown {
    const fs = deps.apiHost.fs;

    const path = (index: number): VfsPath => {
      const value = args[index];
      if (typeof value !== 'string' || value.length === 0) {
        throw badRequest(`fs.${method} wants a path as argument ${index + 1}`);
      }
      return value;
    };

    switch (method) {
      case 'list':
        return fs.list(ctx, path(0));
      case 'read':
        return fs.read(ctx, path(0));
      case 'readText':
        return fs.readText(ctx, path(0));
      case 'write': {
        const data = args[1];
        if (typeof data === 'string') return fs.write(ctx, path(0), data);
        // `instanceof Uint8Array` is not usable: the bytes were constructed in the *app's* realm.
        // What matters is that they are a view over bytes, so that is what is checked.
        if (ArrayBuffer.isView(data)) {
          return fs.write(ctx, path(0), new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
        }
        throw badRequest('fs.write wants a string or bytes');
      }
      case 'mkdir':
        return fs.mkdir(ctx, path(0));
      case 'move':
        return fs.move(ctx, path(0), path(1));
      case 'remove':
        return fs.remove(ctx, path(0));
      case 'versions':
        return fs.versions(ctx, path(0));
      case 'restore': {
        const versionId = args[1];
        if (typeof versionId !== 'string' || versionId.length === 0) {
          throw badRequest('fs.restore wants a version id');
        }
        return fs.restore(ctx, path(0), versionId);
      }
      case 'pickOpen':
        return fs.pickOpen(ctx, pickOptsFrom(args[0]));
      case 'pickSave':
        return fs.pickSave(ctx, pickOptsFrom(args[0]));
      case 'watch':
        return startWatch(connection, ctx, path(0));
      case 'unwatch':
        // Not a `HangarAPI.fs` method: a callback cannot cross a `postMessage`, so the client's
        // `watch` returns a subscription id and this is how it lets go of it.
        return stopWatch(connection.instanceKey, args[0]);
      default:
        throw badRequest(`fs has no method '${method}'`);
    }
  }

  async function startWatch(
    connection: BridgeConnection,
    ctx: AppInstanceContext,
    dir: VfsPath,
  ): Promise<string> {
    const id = `sub${(nextSubscription += 1)}`;
    const instanceKey = connection.instanceKey;
    const live = subscriptions.get(instanceKey) ?? new Map<string, () => void>();
    subscriptions.set(instanceKey, live);
    // The slot is claimed *before* the await. `watch` settles asynchronously, and a window that
    // closes — or an `unwatch` that arrives — while it does must not leave a live watcher pushing
    // `fs/changed` at a frame that is gone.
    live.set(id, () => undefined);

    const stop = await Promise.resolve(
      deps.apiHost.fs.watch(ctx, dir, (event) => {
        connection.send('fs/changed', { ...event, subscription: id });
      }),
    );

    if (subscriptions.get(instanceKey) !== live || !live.has(id)) {
      stop();
      return id;
    }
    live.set(id, stop);
    return id;
  }

  function stopWatch(instanceKey: string, raw: unknown): void {
    if (typeof raw !== 'string' || raw.length === 0) {
      throw badRequest('fs.unwatch wants a subscription id');
    }
    const live = subscriptions.get(instanceKey);
    const stop = live?.get(raw);
    live?.delete(raw);
    if (live && live.size === 0) subscriptions.delete(instanceKey);
    stop?.();
  }

  const route = (connection: BridgeConnection, message: DecodedMessage): void => {
    const winId = deps.winIdFor(connection.instanceKey);
    // The window can close between a message being posted and being read. There is nothing left to
    // do it *to*, and the connection is on its way out with it.
    if (winId === undefined) return;

    const ctx: AppInstanceContext = {
      appId: connection.appId,
      winId,
      instanceKey: connection.instanceKey,
    };
    const api = deps.apiHost;

    switch (message.key) {
      // ── window, app→shell (M8.8) ────────────────────────────────────────────────────────
      // Emit-shaped: §4.1's setters return void and the client does not wait on them.
      case 'window/title':
        void api.window.setTitle(ctx, (message.payload as { title: string }).title);
        return;
      case 'window/icon':
        void api.window.setIcon(ctx, (message.payload as { svg: string }).svg);
        return;
      case 'window/dirty':
        void api.window.setDirty(ctx, (message.payload as { dirty: boolean }).dirty);
        return;
      case 'window/busy':
        void api.window.setBusy(ctx, (message.payload as { busy: boolean }).busy);
        return;
      case 'window/close':
        // The app closing *itself* is not vetoable — it is the thing that would have vetoed.
        void api.window.close(ctx);
        return;

      // Call-shaped: the client is awaiting `window/reply`.
      case 'window/openWindow':
        respond(connection, message, 'window/reply', () =>
          api.window.open(ctx, (message.payload as { arg?: { title?: string } }).arg),
        );
        return;
      case 'window/launchApp':
        respond(connection, message, 'window/reply', () =>
          api.launch.app(ctx, (message.payload as { appId: string }).appId),
        );
        return;
      case 'window/openWith':
        respond(connection, message, 'window/reply', () => {
          const body = message.payload as { path: string; appId?: string };
          return api.launch.openWith(ctx, body.path as VfsPath, body.appId);
        });
        return;
      case 'window/openedPath':
        respond(connection, message, 'window/reply', () => api.launch.openedPath(ctx));
        return;

      // ── menu, app→shell (M8.9) ──────────────────────────────────────────────────────────
      // `command/fire` is the other half and travels shell→app; the shell sends it when a user
      // picks an item, so there is nothing inbound for it.
      case 'menu/publish':
        respond(connection, message, 'menu/reply', () =>
          api.menu.publish(ctx, (message.payload as { tree: MenuNode[] }).tree),
        );
        return;

      // ── fs, app→shell (M8.10) ───────────────────────────────────────────────────────────
      // §9.3: "every `HangarAPI.fs` method, permission-checked per call". One message key for the
      // whole group, because §9.3's channel table is closed — the method rides in the payload.
      case 'fs/call': {
        const body = message.payload as { method: string; args: unknown[] };
        respond(connection, message, 'fs/reply', () =>
          callFs(connection, ctx, body.method, body.args),
        );
        return;
      }

      // ── dialog, app→shell (M8.8, D-120) ─────────────────────────────────────────────────
      // §19.4 — the shell draws all three. An app cannot fake one, and a modal from a background
      // window cannot steal the screen, because it is the shell deciding where it goes.
      case 'dialog/alert':
        respond(connection, message, 'dialog/reply', () =>
          api.dialog.alert(ctx, (message.payload as { message: string }).message),
        );
        return;
      case 'dialog/confirm':
        respond(connection, message, 'dialog/reply', () =>
          api.dialog.confirm(ctx, (message.payload as { message: string }).message),
        );
        return;
      case 'dialog/prompt':
        respond(connection, message, 'dialog/reply', () => {
          const body = message.payload as { message: string; defaultValue?: string };
          return api.dialog.prompt(ctx, body.message, body.defaultValue);
        });
        return;

      // ── session, both ways (M8.11) ──────────────────────────────────────────────────────
      // §5.6 — an opaque blob, kept per *window instance* and handed straight back on the next
      // boot. The shell never looks inside one, which is what lets an app change its own format
      // without the shell knowing (and what makes a blob it cannot read the app's problem, F12).
      case 'session/save':
        respond(connection, message, 'session/reply', () =>
          api.session.save(ctx, (message.payload as { blob: unknown }).blob),
        );
        return;
      case 'session/load':
        // The one call in §9.3 that does **not** answer on `<channel>/reply`: its table gives
        // `load` a `state{blob}` of its own, so the blob is not wrapped in an `ok`/`value` pair
        // it would have to be unwrapped from. A *failure* still comes back on `session/reply`,
        // because there is no blob to put in a `state`.
        void Promise.resolve()
          .then(() => api.session.load(ctx))
          .then(
            (blob) => {
              connection.send('session/state', { to: message.envelope.id, blob });
            },
            (error: unknown) => {
              connection.send('session/reply', {
                to: message.envelope.id,
                ok: false,
                ...failure(error),
              });
            },
          );
        return;

      // ── transfer, app→shell (M9.2, M9.5) ────────────────────────────────────────────────
      // §10 — one service, three paths. `receive` travels the other way and is not inbound.
      case 'transfer/copy':
        respond(connection, message, 'transfer/reply', () =>
          api.transfer.copy(ctx, wirePayloadIn((message.payload as { payload: Payload }).payload)),
        );
        return;
      case 'transfer/readClipboard':
        // §10.1 — refuses with `PERMISSION_REQUIRES_GESTURE` unless the shell armed this window,
        // and that refusal is the *point*: it travels back as an error the app can act on.
        respond(connection, message, 'transfer/reply', async () =>
          wirePayloadOut(await api.transfer.readClipboard(ctx)),
        );
        return;
      case 'transfer/share':
        respond(connection, message, 'transfer/reply', () =>
          api.transfer.share(ctx, wirePayloadIn((message.payload as { payload: Payload }).payload)),
        );
        return;

      // ── notify, app→shell (M10.1) ───────────────────────────────────────────────────────
      // §13 — `post` returns the id, so it is call-shaped. `notify/action` travels the other way,
      // when the *user* presses a button the shell drew, and is not inbound.
      case 'notify/post':
        respond(connection, message, 'notify/reply', () =>
          api.notify.post(ctx, message.payload as { title: string; body?: string; actions?: string[] }),
        );
        return;

      // ── exec, app→shell (M13.2) ─────────────────────────────────────────────────────────
      // §9.3 / §11.2 — the frames of a command the *shell* asked this app to run. Emit-shaped and
      // deliberately so: a stream is many messages for one request, which a call-shaped reply
      // cannot express (D-166). `to` is the `exec/run` id, so two commands running at once do not
      // interleave into the same tab.
      case 'exec/frame': {
        const { to, frame } = message.payload as { to: string; frame: ExecFrame };
        deps.onExecFrame?.(connection, to, frame);
        return;
      }

      // ── keys, app→shell (M8.12) ─────────────────────────────────────────────────────────
      // §9.7 — a focused iframe gets `keydown` first and the parent cannot observe it, so the
      // *app* matches the reserved set, swallows the event and hands the chord back. Emit-shaped:
      // there is nothing to answer, and the app has already been told not to act on it.
      case 'keys/chord':
        deps.onChord?.(connection, (message.payload as { chord: string }).chord);
        return;

      // `handshake/hello` is answered by the host itself, before this is reached, and
      // `window/closeReply` is a *reply* — the host resolves the waiter and the veto path reads it.
      case 'handshake/hello':
      case 'window/closeReply':
        return;

      default:
        // `auth/ticket` travels shell→app only (M12), and `exec/reply` is the app's immediate
        // acknowledgement, which the exec service reads through `request` rather than here.
        deps.onUnrouted?.(connection, message.key);
        return;
    }
  };

  return Object.assign(route, {
    release(instanceKey: string): void {
      const live = subscriptions.get(instanceKey);
      if (!live) return;
      subscriptions.delete(instanceKey);
      for (const stop of live.values()) {
        try {
          stop();
        } catch {
          // A watcher that throws on its way out is not worth taking a window close down for.
        }
      }
      live.clear();
    },
  });
}
